import { CheckCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import Loader from '@zeus/core/Loader';
import { useCollection } from '_/contexts/CollectionContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import {
  useLinkedCollectionDestinations,
  useLinkDestinationToCollection,
  useUnlinkDestinationFromCollection,
} from '_/hooks/useCollections';
import { useDestinations } from '_/hooks/useDestinations';
import useOrgAccess from '_/hooks/useOrgAccess';
import { IDestination } from '_/types/destinations/destinations.types';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Tooltip, Spin, Table, Checkbox, Switch, message, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useState, ReactElement, useMemo, useEffect, useCallback } from 'react';

function CollectionDestinations(): ReactElement {
  const history = useNavigate();
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const { selectedCollection, selectedCollectionSlug, isLoading: isLoadingCollection } = useCollection();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();
  const { setBreadcrumbs, setOnRefresh, setRefreshLoading } = useTopHeader();

  // React Query hooks
  const { data: destinations = [], isLoading: isLoadingDestinations, refetch: refetchDestinations } = useDestinations();
  const {
    data: linkedCollectionDestinations = [],
    isLoading: isLoadingLinkedDestinations,
    refetch: refetchLinkedDestinations,
    isRefetching: isRefetchingLinkedDestinations,
  } = useLinkedCollectionDestinations(selectedCollection?.id);
  const linkMutation = useLinkDestinationToCollection(selectedCollection?.id);
  const unlinkMutation = useUnlinkDestinationFromCollection(selectedCollection?.id);

  const [editMode, setEditMode] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [linkingDestinationId, setLinkingDestinationId] = useState<string | null>(null);

  const isLoading = isLoadingCollection || isLoadingDestinations || isLoadingLinkedDestinations;

  const handleLinkDestinationToCollection = async (availableDestinationId: string, destinationName?: string) => {
    if (!selectedCollection) return;

    try {
      setLinkingDestinationId(availableDestinationId);
      await linkMutation.mutateAsync(availableDestinationId);
      message.success(`Destination "${destinationName || availableDestinationId}" linked successfully`);
    } catch {
      message.error(`Failed to link destination "${destinationName || availableDestinationId}"`);
    } finally {
      setLinkingDestinationId(null);
    }
  };

  const handleConfirmUnlinkCollectionDestination = async (destinationId: string, destinationName?: string) => {
    if (!selectedCollection) return;

    try {
      setLinkingDestinationId(destinationId);
      await unlinkMutation.mutateAsync(destinationId);
      message.success(`Destination "${destinationName || destinationId}" unlinked successfully`);
    } catch {
      message.error(`Failed to unlink destination "${destinationName || destinationId}"`);
    } finally {
      setLinkingDestinationId(null);
    }
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  const checkCollectionDestination = (e: React.ChangeEvent<HTMLInputElement>, destination: IDestination) => {
    if (e.target.checked) {
      void handleLinkDestinationToCollection(destination.id, destination.name);
    } else {
      void handleConfirmUnlinkCollectionDestination(destination.id, destination.name);
    }
  };

  // Transform destinations like the selector did
  const destinationsForTable = useMemo(() => {
    return (destinations as IDestination[]).map((destination) => ({
      ...destination,
      destinationId: destination.id || '',
      href: `/orgs/${selectedOrgSlug}/destinations/${destination?.name}/edit`,
    }));
  }, [destinations, selectedOrgSlug]);

  const linkedDestinationsForTable = useMemo(() => {
    return (linkedCollectionDestinations as IDestination[]).map((destination) => ({
      ...destination,
      destinationId: destination.id || '',
      href: `/orgs/${selectedOrgSlug}/destinations/${destination?.name}/edit`,
    }));
  }, [linkedCollectionDestinations, selectedOrgSlug]);

  const isDestinationLinked = (destinationId: string) => {
    return linkedDestinationsForTable?.some((dest) => dest.id === destinationId || dest.destinationId === destinationId);
  };

  const handleSelectDestination = (destinationName: string) => {
    if (access) {
      void history({ to: `/orgs/${selectedOrgSlug}/destinations/${destinationName}/edit` });
    }
  };

  const columns: ColumnsType<IDestination> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: IDestination, b: IDestination) => (a.name || '').localeCompare(b.name || ''),
      render: (text: string, record: IDestination) =>
        access ? <a onClick={() => handleSelectDestination(record.name)}>{text}</a> : <span>{text}</span>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: (a: IDestination, b: IDestination) => (a.type || '').localeCompare(b.type || ''),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      render: (active: boolean) => (active ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} /> : null),
    },
    {
      title: 'Linked',
      dataIndex: 'id',
      key: 'linked',
      width: 100,
      fixed: 'right' as const,
      render: (id: string, record: IDestination) => {
        const isLinked = isDestinationLinked(id);
        const isLoading = linkingDestinationId === id;

        if (isLoading) {
          return <Spin size="small" />;
        }

        return editMode ? (
          <Checkbox checked={isLinked} onChange={(e) => checkCollectionDestination(e, record)} />
        ) : isLinked ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
        ) : null;
      },
    },
  ];

  const filteredRowData = searchText
    ? destinationsForTable.filter(
        (dest) =>
          (dest.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (dest.type || '').toLowerCase().includes(searchText.toLowerCase()),
      )
    : destinationsForTable;

  const filteredLinkedDestinations = searchText
    ? linkedDestinationsForTable.filter(
        (dest) =>
          (dest.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
          (dest.type || '').toLowerCase().includes(searchText.toLowerCase()),
      )
    : linkedDestinationsForTable;

  const handleRefresh = useCallback(() => {
    void refetchDestinations();
    void refetchLinkedDestinations();
  }, [refetchDestinations, refetchLinkedDestinations]);

  // Set up refresh functionality
  useEffect(() => {
    setOnRefresh(handleRefresh);
    setRefreshLoading(isRefetchingLinkedDestinations);

    return () => {
      setOnRefresh(undefined);
      setRefreshLoading(false);
    };
  }, [setOnRefresh, setRefreshLoading, isRefetchingLinkedDestinations, handleRefresh]);

  // Set up breadcrumbs
  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      {
        title: 'Collections',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections`,
      },
    ];

    if (selectedCollection) {
      breadcrumbs.push(
        {
          title: selectedCollection.name || '',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/home`,
        },
        { title: 'Destinations' },
      );
    }

    setBreadcrumbs(breadcrumbs);

    return () => {
      setBreadcrumbs([]);
    };
  }, [
    setBreadcrumbs,
    selectedOrganization,
    selectedOrgSlug,
    selectedProject,
    selectedProjectSlug,
    selectedCollection,
    selectedCollectionSlug,
  ]);

  // Show loader while data is loading
  if (isLoading) {
    return <Loader />;
  }

  // If collection is loaded but not found, show error
  if (!selectedCollection) {
    return <div>Collection not found</div>;
  }

  const defaultView =
    linkedDestinationsForTable?.length > 0 ? (
      <Table
        dataSource={filteredLinkedDestinations}
        columns={columns}
        rowKey={(record: IDestination) => record.id || ''}
        scroll={{ x: true }}
        pagination={{
          pageSize: 100,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} destinations`,
        }}
      />
    ) : (
      <p>No Linked Collection Destinations</p>
    );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div style={{ flexGrow: 1 }}>
          {(editMode || linkedDestinationsForTable?.length > 0) && (
            <Input
              placeholder="Search destinations..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ maxWidth: 400 }}
              allowClear
            />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tooltip
            title={
              access
                ? 'Link/Unlink Collection Destinations'
                : 'You do not have the right permissions to link/unlink collection destinations'
            }
          >
            <span>
              <Switch checked={editMode} onChange={toggleEditMode} disabled={!access} />
            </span>
          </Tooltip>
          <span>Linked/All</span>
        </div>
      </div>
      {editMode ? (
        <Table
          dataSource={filteredRowData}
          columns={columns}
          rowKey={(record) => record.id || ''}
          scroll={{ x: true }}
          pagination={{
            pageSize: 100,
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} destinations`,
          }}
        />
      ) : (
        defaultView
      )}
    </div>
  );
}

export default CollectionDestinations;
